import {
  getState,
  useSelector,
  formSelector,
  updateForm,
  onlyIfAutoForm,
} from '../../../../store.js'
import api from '../../../../common/api.js'
import {isPresent} from '../../../../common/utils.js'
import {NEW_ID} from '../../../../common/constants/index.js'
import {DHL_ECOMMERCE} from '../../../../common/constants/ShipperNames.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import ButtonSecondary from '../../../../common/components/Button/ButtonSecondary.js'
import Select from '../../../../common/components/Form/Select.js'
import {setShipper} from '../../../../data/shippers.js'
import {showGlobalError} from '../../../GlobalErrorMessage.js'
import Archive from './Archive.js'
import Nickname from './Nickname.js'
import ShippingMethods from './ShippingMethods.js'
import ShipperOptions from '../../../../data/shipperOptions.js'
import {
  baseSaveParamsSelector,
  baseShipperFormErrorsSelector,
  baseShipperInitialFormSelector,
  goToShipperSettings,
} from '../ShippersFunctions.js'
import className from '../../../../common/className.js'
import TextInput from '../../Common/TextInput.js'

export function setUpShipperForm({shipper, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {shipper}),
      errorsSelector,
    },
  }
}

export function errorsSelector(state, {formName}) {
  const errors = baseShipperFormErrorsSelector(state, {formName})
  const form = formSelector(state, {
    formName,
  })

  if (!isPresent(form.client_id)) {
    errors.client_id = 'Client ID is required'
    errors.preventSave = true
  }

  if (!isPresent(form.client_secret)) {
    errors.client_secret = 'Client Secret is required'
    errors.preventSave = true
  }

  if (!isPresent(form.pickup_account_number)) {
    errors.pickup_account_number = 'Pickup Account Number is required'
    errors.preventSave = true
  }

  if (!isPresent(form.package_prefix)) {
    errors.package_prefix = 'Package Prefix is required'
    errors.preventSave = true
  }

  return errors
}

export function initialFormSelector(state, {shipper}) {
  const vendorConfig = shipper?.vendor_config || {}

  return {
    ...baseShipperInitialFormSelector(state, {shipper, vendor: DHL_ECOMMERCE}),
    client_id: vendorConfig.client_id || '',
    client_secret: vendorConfig.client_secret || '',
    account_number: vendorConfig.account_number || '',
    pickup_account_number: vendorConfig.pickup_account_number || '',
    distribution_center:
      vendorConfig.distribution_center ||
      ShipperOptions[DHL_ECOMMERCE].distribution_center[0].value,
    package_prefix: vendorConfig.package_prefix || '',
  }
}

export async function saveShipperDHLEcommerce(formName) {
  const form = formSelector(getState(), {formName})
  let [shipperID, params] = baseSaveParamsSelector(getState(), {formName})

  const {
    client_id,
    client_secret,
    account_number,
    pickup_account_number,
    distribution_center,
    package_prefix,
  } = form

  params = {
    ...params,
    client_id,
    client_secret,
    account_number,
    pickup_account_number,
    distribution_center,
    package_prefix,
  }

  updateForm(formName, {isSaving: true})

  try {
    const {json} = await (shipperID !== NEW_ID
      ? api.put(`/shipper/${shipperID}/`, params)
      : api.post('/shipper/', {...params, vendor: DHL_ECOMMERCE}))

    goToShipperSettings()

    setShipper(json)
  } catch (err) {
    showGlobalError(
      {
        summary: 'There was an error while saving shipper.',
        details: err.error_message || err.message,
      },
      err,
    )

    updateForm(formName, {isSaving: false})
  }
}

function DHLEcommerceForm({formName, form}) {
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))

  return (
    <div className="settings-list-item-form-wrap clear-both">
      <div className="medium-6 columns">
        <fieldset>
          <ul className="form-list margin-bottom-0">
            {form.id !== NEW_ID && <Nickname formName={formName} />}
            <li
              className={className`form-list-item bigger margin-bottom-20 ${{'margin-top-20 divider--top': form.id !== NEW_ID}}`}
            >
              <TextInput
                label="Client ID"
                name="client_id"
                value={form.client_id}
                onChange={(event) =>
                  updateForm(formName, {
                    client_id: event.target.value,
                    hasChanged_client_id: true,
                  })
                }
                errorMessage={form.hasChanged_client_id && errors.client_id}
                required
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Client Secret"
                name="client_secret"
                value={form.client_secret}
                onChange={(event) =>
                  updateForm(formName, {
                    client_secret: event.target.value,
                    hasChanged_client_secret: true,
                  })
                }
                errorMessage={
                  form.hasChanged_client_secret && errors.client_secret
                }
                required
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Account Number"
                name="account_number"
                value={form.account_number}
                onChange={(event) =>
                  updateForm(formName, {
                    account_number: event.target.value,
                    hasChanged_account_number: true,
                  })
                }
                errorMessage={
                  form.hasChanged_account_number && errors.account_number
                }
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Pickup Account Number"
                name="pickup_account_number"
                value={form.pickup_account_number}
                onChange={(event) =>
                  updateForm(formName, {
                    pickup_account_number: event.target.value,
                    hasChanged_pickup_account_number: true,
                  })
                }
                errorMessage={
                  form.hasChanged_pickup_account_number &&
                  errors.pickup_account_number
                }
                required
              />
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <label htmlFor="distribution_center">Distribution Center</label>
              <Select
                name="distribution_center"
                value={form.distribution_center}
                onChange={(value) =>
                  updateForm(formName, {distribution_center: value})
                }
              >
                {ShipperOptions[DHL_ECOMMERCE].distribution_center.map(
                  ({value, display}) => (
                    <option key={value} value={value}>
                      {display}
                    </option>
                  ),
                )}
              </Select>
            </li>
            <li className="form-list-item bigger margin-bottom-20">
              <TextInput
                label="Package Prefix"
                name="package_prefix"
                value={form.package_prefix}
                onChange={(event) =>
                  updateForm(formName, {
                    package_prefix: event.target.value,
                    hasChanged_package_prefix: true,
                  })
                }
                errorMessage={
                  form.hasChanged_package_prefix && errors.package_prefix
                }
                required
              />
            </li>
            {form.id !== NEW_ID && <ShippingMethods formName={formName} />}
            <li className="list__item list__item--no-style">
              <ButtonPrimary
                onClick={() => saveShipperDHLEcommerce(formName)}
                isDisabled={errors.preventSave}
                isLoading={form.isSaving}
              >
                {form.id !== NEW_ID ? 'Save' : 'Create'}
              </ButtonPrimary>
              <ButtonSecondary onClick={() => goToShipperSettings()}>
                Cancel
              </ButtonSecondary>
            </li>
          </ul>
        </fieldset>
      </div>
      <div className="medium-5 columns">
        <fieldset>
          <div className="alert margin-bottom-10 alert--standard">
            {form.id === NEW_ID ? (
              <dl className="list">
                <dt className="list__title fs-00 lh-md margin-bottom-5">
                  Need help authorizing your DHL eCommerce account?
                </dt>
                <dd className="list__item fs-n0 margin-bottom-0">
                  <a
                    className="btn--link mid meta-ups-setup-help"
                    href="https://support.ordoro.com/how-do-i-set-up-dhl-ecommerce-in-ordoro/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get step-by-step instructions →
                  </a>
                </dd>
              </dl>
            ) : (
              <Archive formName={formName} />
            )}
          </div>
        </fieldset>
      </div>
    </div>
  )
}

export default onlyIfAutoForm(DHLEcommerceForm, setUpShipperForm)
